import React, { Component } from 'react';
import ReactDOM from 'react-dom';

import { FrameContextProvider } from './Context';
import Content from './Content';

const IFRAME_INITIAL_CONTENT = `
<!DOCTYPE html>
<html>
  <head>
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <style>
      html, body {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
        font-size: 13px;
        box-sizing: border-box;
        margin: 0;
        padding: 0;
      }
      h1, h2, h3, h4, h5 {
        margin: 0;
        padding: 0;
        font-size: 100%;
      }
      body *, *:before, *:after {
        box-sizing: inherit;
      }
      input, textarea, select, button {
        font-size: 1rem;
        font-family: inherit;
      }
      body {
        line-height: 1;
      }
    </style>
  </head>
  <body>
    <div></div>
  </body>
</html>
`;

// Taken from https://github.com/ryanseddon/react-frame-component
// with fixes for Preact (portal cannot unmmount multiple sibling
// child components)
export default class Frame extends Component {
  static defaultProps = {
    style: {},
    head: null,
    children: undefined,
    mountTarget: undefined,
    contentDidMount: () => {},
    contentDidUpdate: () => {},
    initialContent: IFRAME_INITIAL_CONTENT,
  };

  constructor(props, context) {
    super(props, context);
    this._isMounted = false;
    this.state = { iframeLoaded: false };
  }

  componentDidMount() {
    this._isMounted = true;

    const doc = this.getDoc();
    if (doc && doc.readyState === 'complete') {
      this.forceUpdate();
    } else {
      this.node.addEventListener('load', this.handleLoad);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;

    this.node.removeEventListener('load', this.handleLoad);
  }

  getDoc() {
    return this.node ? this.node.contentDocument : null; // eslint-disable-line
  }

  getMountTarget() {
    const doc = this.getDoc();
    if (this.props.mountTarget) {
      return doc.querySelector(this.props.mountTarget);
    }
    return doc.body.children[0];
  }

  setRef = (node) => {
    this.node = node;
  };

  handleLoad = () => {
    this.setState({ iframeLoaded: true });
  };

  renderFrameContents() {
    if (!this._isMounted) {
      return null;
    }

    const doc = this.getDoc();

    if (!doc) {
      return null;
    }

    const contentDidMount = this.props.contentDidMount;
    const contentDidUpdate = this.props.contentDidUpdate;

    const win = doc.defaultView || doc.parentView;
    const contents = (
      <Content
        contentDidMount={contentDidMount}
        contentDidUpdate={contentDidUpdate}
      >
        <FrameContextProvider value={{ document: doc, window: win }}>
          <div className="frame-content">{this.props.children}</div>
        </FrameContextProvider>
      </Content>
    );

    const mountTarget = this.getMountTarget();

    return ReactDOM.createPortal(contents, mountTarget);
  }

  render() {
    const props = {
      ...this.props,
      srcDoc: this.props.initialContent,
      children: undefined, // The iframe isn't ready so we drop children from props here. #12, #17
    };
    delete props.head;
    delete props.initialContent;
    delete props.mountTarget;
    delete props.contentDidMount;
    delete props.contentDidUpdate;
    return (
      <iframe
        title={'gb-frame'}
        {...props}
        ref={this.setRef}
        onLoad={this.handleLoad}
      >
        {this.state.iframeLoaded && this.renderFrameContents()}
      </iframe>
    );
  }
}
